package com.yendo.mx.ui.home

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.LocationOff
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.Star
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.Producto
import com.yendo.mx.models.SeccionHome
import com.yendo.mx.models.DireccionGuardada
import com.yendo.mx.repository.RestauranteRepository
import com.yendo.mx.repository.HomeRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.ui.components.BuscadorYendo
import com.yendo.mx.ui.components.RestauranteRatingBadge
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.EstadoCarga
import com.yendo.mx.utils.validarRestauranteAbierto
import com.yendo.mx.utils.getAhora
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.launch
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import coil3.size.Size

import org.jetbrains.compose.resources.ExperimentalResourceApi
import yendo.shared.generated.resources.*
import io.github.alexzhirkevich.compottie.Compottie
import io.github.alexzhirkevich.compottie.LottieCompositionSpec
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter

import com.yendo.mx.utils.BackHandler
import com.yendo.mx.utils.esAndroid

// 🚀 FUNCIÓN COMPATIBLE MULTIPLATAFORMA (ANDROID, IOS, WEB) PARA LIMPIEZA DE TEXTO (0ms CPU, 0% CRASH EN JS)
private fun String.limpiarTextoCategoriaComercios(): String {
    return this.filter { it.isLetterOrDigit() || it.isWhitespace() }.trim()
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun VistaComercios(
    listState: LazyListState = rememberSaveable(saver = LazyListState.Saver) { LazyListState() },
    direccionActiva: DireccionGuardada? = null,
    onAbrirUbicacion: () -> Unit = {},
    onRestauranteClick: (RestauranteFirebase) -> Unit,
    onProductoDirectoClick: (Producto, RestauranteFirebase) -> Unit = { _, _ -> },
    onVerTodoSeccion: (SeccionHome) -> Unit = {},
    onAbrirBuscador: () -> Unit = {},
    onBack: () -> Unit = {}
) {
    BackHandler(enabled = true) { onBack() }
    val scope = rememberCoroutineScope()
    val estadoConfig by ConfigManager.estadoConfig.collectAsState()

    var categoriaSeleccionada by rememberSaveable { mutableStateOf("Todos") }
    var textoBusqueda by remember { mutableStateOf(TextFieldValue("")) }
    var paginaActual by rememberSaveable { mutableIntStateOf(1) }

    // 🚀 REINICIAR A PÁGINA 1 AL CAMBIAR DE BÚSQUEDA, CATEGORÍA O ZONA
    LaunchedEffect(textoBusqueda.text, categoriaSeleccionada, estadoConfig) {
        paginaActual = 1
    }

    val idZonaActual = (estadoConfig as? EstadoCarga.Exito)?.id_zona

    // 🚀 INTEGRACIÓN CERO-LATENCIA CON BACKEND BFF EN CLOUD FUNCTIONS (Candado Anti-Refresco Repetido):
    val initialCatalog = remember(idZonaActual) {
        if (idZonaActual != null) HomeRepository.obtenerCacheCatalogo(idZonaActual, "COMERCIOS")
        else com.yendo.mx.repository.RespuestaCatalogoCloud()
    }
    val catalogoCloudState by produceState<com.yendo.mx.repository.RespuestaCatalogoCloud>(initialValue = initialCatalog, key1 = idZonaActual) {
        if (idZonaActual != null && value.negocios.isEmpty()) {
            value = HomeRepository.obtenerCatalogoCompletoCloud(idZonaActual, vistaTarget = "COMERCIOS")
        }
    }

    // 🚀 DESCONEXIÓN DE LISTENER PESADO EN COLD START:
    // Evita descargar cientos de documentos crudos por WebSocket si la Nube responde en 0ms
    val restaurantesBase by if (catalogoCloudState.negocios.isEmpty()) {
        RestauranteRepository.obtenerRestaurantes().collectAsState(initial = emptyList())
    } else {
        remember { mutableStateOf(emptyList<RestauranteFirebase>()) }
    }

    val seccionesComercioBase by if (catalogoCloudState.secciones.isEmpty()) {
        HomeRepository.observarSeccionesPorVista(idZonaActual ?: "", "COMERCIOS").collectAsState(initial = emptyList())
    } else {
        remember { mutableStateOf(emptyList<SeccionHome>()) }
    }

    val seccionesComercio = if (catalogoCloudState.secciones.isNotEmpty()) catalogoCloudState.secciones else seccionesComercioBase
    val restaurantes = if (catalogoCloudState.negocios.isNotEmpty()) catalogoCloudState.negocios else restaurantesBase

    // 🚀 PRECARGA SOMBRA DE IMÁGENES EN SEGUNDO PLANO (Solo Android):
    val platformContext = LocalPlatformContext.current
    if (esAndroid()) {
        LaunchedEffect(restaurantes) {
            if (restaurantes.isNotEmpty()) {
                withContext(Dispatchers.Default) {
                    try {
                        val imageLoader = coil3.SingletonImageLoader.get(platformContext)
                        restaurantes.take(12).forEach { rest ->
                            val url = rest.infoPublica.logotipo.ifEmpty { rest.infoPublica.header }
                            if (url.isNotEmpty()) {
                                val req = ImageRequest.Builder(platformContext)
                                    .data(url)
                                    .size(350, 350)
                                    .memoryCachePolicy(coil3.request.CachePolicy.ENABLED)
                                    .diskCachePolicy(if (esAndroid()) coil3.request.CachePolicy.ENABLED else coil3.request.CachePolicy.DISABLED)
                                    .build()
                                imageLoader.enqueue(req)
                            }
                        }
                    } catch (_: Exception) { }
                }
            }
        }
    }

    val isDark = isSystemInDarkTheme()
    val colorFondoPegajoso = if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8)
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray

    // =================================================================
    // 🚀 CHIPS DINÁMICOS ASÍNCRONOS CON CACHÉ SÍNCRONA DE NAVEGACIÓN (0ms de parpadeo)
    // =================================================================
    val initialChips = remember(idZonaActual) { cacheChipsVista["COMERCIOS_$idZonaActual"] ?: listOf(ChipFiltro("Todos", "🏪")) }
    val chipsDinamicos by produceState<List<ChipFiltro>>(
        initialValue = initialChips,
        estadoConfig,
        restaurantes
    ) {
        value = withContext(Dispatchers.Default) {
            val configZona = ConfigManager.configActiva
            val listaChips = mutableListOf<ChipFiltro>()
            listaChips.add(ChipFiltro("Todos", "🏪"))

            if (idZonaActual != null) {
                val categoriasVivas = mutableSetOf<String>()
                val subcategoriasVivas = mutableSetOf<String>()

                restaurantes.filter {
                    it.ubicacion.idZona == idZonaActual &&
                            it.infoPublica.tipoNegocio == "COMERCIO" &&
                            it.operacion.visible &&
                            it.operacion.activoComercio &&
                            !it.operacion.bloqueadoPorAdmin &&
                            !(it.operacion.pausadoManualmente && it.operacion.pausaHasta > getAhora()) // 🚀 JERARQUÍA DE PAUSA
                }.forEach { rest ->
                    val catLimpia = rest.infoPublica.categoria.limpiarTextoCategoriaComercios()
                    if (catLimpia.isNotBlank()) categoriasVivas.add(catLimpia)

                    rest.infoPublica.subcategorias.forEach { sub ->
                        val subLimpia = sub.limpiarTextoCategoriaComercios()
                        if (subLimpia.isNotBlank()) subcategoriasVivas.add(subLimpia)
                    }
                }

                val catalogoMaestro = configZona?.clasificacion?.comercios ?: emptyList()

                // 🚀 ORDENACIÓN BASADA EN CATÁLOGO MAESTRO (COMERCIOS)
                catalogoMaestro.filter { it.activa }.forEach { catMaestra ->
                    if (categoriasVivas.any { it.equals(catMaestra.nombre, ignoreCase = true) }) {
                        listaChips.add(ChipFiltro(nombre = catMaestra.nombre, emoji = catMaestra.emoji))
                    }
                }

                categoriasVivas.forEach { nombreCat ->
                    if (listaChips.none { it.nombre.equals(nombreCat, ignoreCase = true) }) {
                        listaChips.add(ChipFiltro(nombre = nombreCat, emoji = ""))
                    }
                }

                cacheChipsVista["COMERCIOS_$idZonaActual"] = listaChips
            }
            listaChips
        }
    }

    // =================================================================
    // 🚀 FILAS DE COMERCIOS CON REMEMBER SÍNCRONO (0ms latencia para cero-clamping de scroll)
    // =================================================================
    val filas = remember(restaurantes, textoBusqueda.text, categoriaSeleccionada, idZonaActual, paginaActual) {
        if (idZonaActual == null) {
            emptyList()
        } else {
            val tiempoActualMs = getAhora()

            val filtrados = restaurantes.filter { rest ->
                if (!rest.operacion.visible || !rest.operacion.activoComercio || rest.operacion.bloqueadoPorAdmin) return@filter false
                if (rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > tiempoActualMs) return@filter false // 🚀 OCULTAR SI ESTÁ PAUSADO
                if (rest.infoPublica.tipoNegocio.isNotBlank() && rest.infoPublica.tipoNegocio != "COMERCIO" && rest.infoPublica.tipoNegocio != "TIENDITA") return@filter false

                val idZonaNegocio = rest.ubicacion.idZona.ifBlank { idZonaActual }
                val coincideZona = idZonaNegocio == idZonaActual
                val nombre = rest.infoPublica.nombre
                val catLimpia = rest.infoPublica.categoria.limpiarTextoCategoriaComercios()

                val coincideTexto = textoBusqueda.text.isBlank() ||
                        nombre.contains(textoBusqueda.text, ignoreCase = true) ||
                        catLimpia.contains(textoBusqueda.text, ignoreCase = true) ||
                        rest.infoPublica.subcategorias.any { it.contains(textoBusqueda.text, ignoreCase = true) }

                val coincideCategoria = categoriaSeleccionada == "Todos" ||
                        catLimpia.equals(categoriaSeleccionada, ignoreCase = true) ||
                        rest.infoPublica.subcategorias.any {
                            it.limpiarTextoCategoriaComercios().equals(categoriaSeleccionada, ignoreCase = true)
                        }

                coincideZona && coincideTexto && coincideCategoria
            }.sortedBy { rest ->
                val pausaHasta = rest.operacion.pausaHasta
                val estaEnPanico = rest.operacion.pausadoManualmente && pausaHasta > tiempoActualMs
                val estaAbiertoNormal = validarRestauranteAbierto(rest)

                val operacionActiva = estaAbiertoNormal && !estaEnPanico

                if (operacionActiva) 0 else 1
            }

            // 🚀 PAGINACIÓN PROGRESIVA: 12 locales (6 filas) por página
            val limiteAMostrar = (paginaActual * 12).coerceAtMost(filtrados.size)
            filtrados.take(limiteAMostrar).chunked(2).map { par ->
                val idKey = if (par.size == 2) "${par[0].id}_${par[1].id}" else par[0].id
                FilaRestaurantesState(idKey = idKey, restaurantes = par)
            }
        }
    }

    LaunchedEffect(chipsDinamicos) {
        if (chipsDinamicos.none { it.nombre == categoriaSeleccionada }) {
            categoriaSeleccionada = "Todos"
        }
    }

    // 🚀 DETECTOR DE SCROLL INFINITO SEGURO (derivedStateOf con aduana estricta)
    val cercaDelFinal by remember(listState) {
        derivedStateOf {
            val total = listState.layoutInfo.totalItemsCount
            val visibleItems = listState.layoutInfo.visibleItemsInfo
            val lastIndex = visibleItems.lastOrNull()?.index ?: -1
            total >= 6 && lastIndex >= total - 2
        }
    }

    LaunchedEffect(cercaDelFinal) {
        if (cercaDelFinal && filas.isNotEmpty()) {
            println("YENDO_PERF_LOG COMERCIOS: Incrementando paginaActual de $paginaActual a ${paginaActual + 1}")
            paginaActual++
        }
    }

    LazyColumn(
        state = listState,
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(top = 0.dp, bottom = 140.dp)
    ) {
        // 🚀 CABECERA DE BÚSQUEDA Y UBICACIÓN (item estándar para 0ms re-medición en scroll)
        item(key = "buscador_cabecera") {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(color = colorFondoPegajoso, shape = RoundedCornerShape(bottomStart = 24.dp, bottomEnd = 24.dp))
                    .padding(top = 4.dp, bottom = 12.dp)
            ) {
                // 🚀 BARRA DE UBICACIÓN COMPACTA
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onAbrirUbicacion() }
                        .padding(horizontal = 20.dp, vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = Icons.Default.LocationOn,
                        contentDescription = null,
                        tint = RosaYendo,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(Modifier.width(6.dp))

                    val tituloUbicacion = direccionActiva?.titulo?.ifBlank { direccionActiva.tipoUbicacion } ?: "Ubicación"
                    val calleSolo = direccionActiva?.direccion?.substringBefore(",")?.trim() ?: ""
                    val textoMostrar = if (calleSolo.isNotBlank()) "$tituloUbicacion • $calleSolo" else tituloUbicacion

                    Text(
                        text = textoMostrar,
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = colorTextoPrincipal,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false)
                    )
                    Spacer(Modifier.width(4.dp))
                    Icon(
                        imageVector = Icons.Default.KeyboardArrowDown,
                        contentDescription = "Cambiar ubicación",
                        tint = colorTextoPrincipal,
                        modifier = Modifier.size(16.dp)
                    )
                }

                Box(modifier = Modifier.fillMaxWidth().clickable { onAbrirBuscador() }) {
                    BuscadorYendo(
                        valor = textoBusqueda,
                        alCambiar = { 
                            if (it.text.isNotBlank()) onAbrirBuscador()
                            else textoBusqueda = it
                        },
                        focoAutomatico = false,
                        onBack = onBack
                    )
                }
            }
        }

        if (chipsDinamicos.size > 1) {
            item(key = "categorias") {
                CategoriasHorizontal(
                    listaChips = chipsDinamicos,
                    categoriaActual = categoriaSeleccionada,
                    onCategoriaSeleccionada = { nuevaCat ->
                        if (categoriaSeleccionada != nuevaCat) {
                            categoriaSeleccionada = nuevaCat
                            scope.launch {
                                listState.animateScrollToItem(0)
                            }
                        }
                    }
                )
                Spacer(modifier = Modifier.height(16.dp))
            }
        }

        // 🚀 BANNERS DINÁMICOS Y CARROUSELES DE VISTA COMERCIOS (Solo en la categoría "Todos")
        if (categoriaSeleccionada == "Todos" && seccionesComercio.isNotEmpty()) {
            items(
                items = seccionesComercio,
                key = { seccion -> "sec_${seccion.id}" },
                contentType = { seccion -> "seccion_${seccion.tipoBloque}" }
            ) { seccion ->
                if (seccion.activo) {
                    when (seccion.tipoBloque) {
                        "BANNERS" -> {
                            if (seccion.listaBanners.isNotEmpty()) {
                                BannersDinamicos(
                                    banners = seccion.listaBanners,
                                    onAbrirNegocio = { idNegocio ->
                                        scope.launch {
                                            try {
                                                val doc = Firebase.firestore.collection("Negocios").document(idNegocio).get()
                                                if (doc.exists) {
                                                    val rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                                                    if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAbrirProducto = { idProducto ->
                                        scope.launch {
                                            try {
                                                val prodDoc = Firebase.firestore.collection("Productos").document(idProducto).get()
                                                if (prodDoc.exists) {
                                                    val prod = prodDoc.data<Producto>().copy(id = prodDoc.id)
                                                    val restDoc = Firebase.firestore.collection("Negocios").document(prod.idRestaurante).get()
                                                    if (restDoc.exists) {
                                                        val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                        if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                    }
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAplicarCupon = { }
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                            }
                        }
                        "BANNERS_CUADRADOS" -> {
                            if (seccion.listaBanners.isNotEmpty()) {
                                BannersCuadrados(
                                    seccion = seccion,
                                    onAbrirNegocio = { idNegocio ->
                                        scope.launch {
                                            try {
                                                val doc = Firebase.firestore.collection("Negocios").document(idNegocio).get()
                                                if (doc.exists) {
                                                    val rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                                                    if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAbrirProducto = { idProducto ->
                                        scope.launch {
                                            try {
                                                val prodDoc = Firebase.firestore.collection("Productos").document(idProducto).get()
                                                if (prodDoc.exists) {
                                                    val prod = prodDoc.data<Producto>().copy(id = prodDoc.id)
                                                    val restDoc = Firebase.firestore.collection("Negocios").document(prod.idRestaurante).get()
                                                    if (restDoc.exists) {
                                                        val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                        if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                    }
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAplicarCupon = { }
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                            }
                        }
                        "BANNERS_CUADRADOS_FULL" -> {
                            if (seccion.listaBanners.isNotEmpty()) {
                                BannersCuadradosFull(
                                    seccion = seccion,
                                    onAbrirNegocio = { idNegocio ->
                                        scope.launch {
                                            try {
                                                val doc = Firebase.firestore.collection("Negocios").document(idNegocio).get()
                                                if (doc.exists) {
                                                    val rest = doc.data<RestauranteFirebase>().copy(id = doc.id)
                                                    if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAbrirProducto = { idProducto ->
                                        scope.launch {
                                            try {
                                                val prodDoc = Firebase.firestore.collection("Productos").document(idProducto).get()
                                                if (prodDoc.exists) {
                                                    val prod = prodDoc.data<Producto>().copy(id = prodDoc.id)
                                                    val restDoc = Firebase.firestore.collection("Negocios").document(prod.idRestaurante).get()
                                                    if (restDoc.exists) {
                                                        val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                        if (rest.operacion.visible && rest.operacion.activoComercio) onRestauranteClick(rest)
                                                    }
                                                }
                                            } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                        }
                                    },
                                    onAplicarCupon = { }
                                )
                                Spacer(modifier = Modifier.height(12.dp))
                            }
                        }
                        "CARRUSEL_CUADRADO" -> {
                            EnchufeCarruselCuadrado(
                                seccion = seccion,
                                idZonaActual = idZonaActual ?: "",
                                colorTexto = colorTextoPrincipal,
                                onVerTodo = onVerTodoSeccion,
                                onProductoClick = { producto ->
                                    scope.launch {
                                        try {
                                            val restDoc = Firebase.firestore.collection("Negocios").document(producto.idRestaurante).get()
                                            if (restDoc.exists) {
                                                val rest = restDoc.data<RestauranteFirebase>().copy(id = restDoc.id)
                                                val ahora = getAhora()
                                                val estaPausado = rest.operacion.pausadoManualmente && rest.operacion.pausaHasta > ahora
                                                val esVisible = rest.operacion.visible && rest.operacion.activoComercio && !rest.operacion.bloqueadoPorAdmin && !estaPausado

                                                if (esVisible) {
                                                    val estaAbierto = rest.operacion.estado == "ACTIVO" && !estaPausado
                                                    val permiteProgramar = rest.operacion.programadoActivo
                                                    if (estaAbierto || permiteProgramar) {
                                                        onProductoDirectoClick(producto, rest)
                                                    } else {
                                                        onRestauranteClick(rest)
                                                    }
                                                }
                                            }
                                        } catch (e: Exception) { println("🚨 Error: ${e.message}") }
                                    }
                                }
                            )
                            Spacer(modifier = Modifier.height(12.dp))
                        }
                        "CARRUSEL_LARGO" -> {
                            EnchufeCarruselLargo(
                                seccion = seccion,
                                idZonaActual = idZonaActual ?: "",
                                colorTexto = colorTextoPrincipal,
                                onVerTodo = onVerTodoSeccion,
                                onRestauranteClick = { rest -> onRestauranteClick(rest) }
                            )
                            Spacer(modifier = Modifier.height(12.dp))
                        }
                    }
                }
            }
        }

        when {
            estadoConfig is EstadoCarga.Cargando || (filas.isEmpty() && restaurantes.isEmpty()) -> {
                item(key = "shimmer_banner") {
                    com.yendo.mx.ui.components.BannerShimmerSkeleton(isDark = isDark)
                }
                item(key = "shimmer_chips") {
                    com.yendo.mx.ui.components.CategoriasChipsShimmerSkeleton(isDark = isDark)
                }
                items(count = 3, key = { "shimmer_comercio_$it" }, contentType = { "shimmer" }) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 6.dp),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        com.yendo.mx.ui.components.RestauranteCardShimmerSkeleton(isDark = isDark, modifier = Modifier.weight(1f))
                        com.yendo.mx.ui.components.RestauranteCardShimmerSkeleton(isDark = isDark, modifier = Modifier.weight(1f))
                    }
                }
            }
            idZonaActual == null || estadoConfig is EstadoCarga.Error -> {
                item {
                    Column(modifier = Modifier.fillMaxWidth().padding(32.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Default.LocationOff, null, modifier = Modifier.size(60.dp), tint = colorTextoSecundario)
                        Spacer(modifier = Modifier.height(16.dp))
                        Text("Fuera de Cobertura", fontWeight = FontWeight.Black, fontSize = 20.sp, color = colorTextoPrincipal)
                        Text("Actualmente no tenemos comercios disponibles aquí.", textAlign = TextAlign.Center, color = colorTextoSecundario, modifier = Modifier.padding(top = 8.dp, bottom = 24.dp))
                    }
                }
            }
            filas.isEmpty() -> {
                item { Box(modifier = Modifier.fillMaxWidth().height(150.dp), contentAlignment = Alignment.Center) { Text("No hay comercios disponibles aquí 😅", color = colorTextoSecundario, fontWeight = FontWeight.Bold) } }
            }
            else -> {
                item(key = "titulo_seccion") {
                    Text(if (categoriaSeleccionada == "Todos") "Comercios Destacados" else categoriaSeleccionada, fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = colorTextoPrincipal, modifier = Modifier.padding(horizontal = 16.dp))
                    Spacer(modifier = Modifier.height(16.dp))
                }

                items(
                    items = filas,
                    key = { fila -> fila.idKey },
                    contentType = { "comercios_row" }
                ) { fila ->
                    val configZonaVal = com.yendo.mx.utils.ConfigManager.configActiva
                    val deliveryZonaOk = (configZonaVal?.logistica?.delivery_activo ?: true) && com.yendo.mx.utils.LogisticaUtils.estaPlataformaAbierta(configZonaVal)
                    Row(
                        modifier = Modifier.animateItem().fillMaxWidth().padding(horizontal = 16.dp), 
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        fila.restaurantes.forEach { rest ->
                            val onClickCard = remember(rest.id) { { onRestauranteClick(rest) } }
                            CardRestauranteCuadrado(
                                rest = rest, 
                                modifier = Modifier.weight(1f), 
                                deliveryGlobalZonaActivo = deliveryZonaOk,
                                onClick = onClickCard
                            )
                        }
                        if (fila.restaurantes.size == 1) Spacer(modifier = Modifier.weight(1f))
                    }
                    Spacer(modifier = Modifier.height(16.dp))
                }
            }
        }
    }
}
